import { mt } from '@passionware/monads';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormRootElement
} from 'v5/design-system/Form';
import { Input } from 'v5/design-system/Input';
import { FormSaveButton } from '../../../v5/patterns/forms/dialogs/FormSaveButton';

const RegisterUserPanel = ({ onSubmit, invitedEmail, isLoading }) => {
  const { t } = useTranslation('v1-features');

  const form = useForm({
    defaultValues: {
      email: invitedEmail,
      password: '',
      confirmedPassword: ''
    },
    mode: 'onBlur'
  });

  return (
    <div className="tailwind-root contents">
      <Form {...form}>
        <FormRootElement
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6 w-[350px]"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="font-semibold text-fg-default">
                  {t('RegisterUserPanel.emailLabel')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    size="md"
                    readOnly
                    aria-readonly="false"
                    {...field}
                  />
                </FormControl>
                <TranslatedFormMessage error={fieldState.error} />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            rules={{
              required: 'RegisterUserPanel.errors.passwordRequired',
              minLength: {
                value: 8,
                message: 'RegisterUserPanel.errors.passwordMinLength'
              },
              validate: {
                hasUpperCase: value =>
                  /[A-Z]/.test(value) ||
                  'RegisterUserPanel.errors.passwordUpperCase'
              }
            }}
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="font-semibold text-fg-default">
                  {t('RegisterUserPanel.passwordLabel')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    size="md"
                    placeholder={t('RegisterUserPanel.passwordPlaceholder')}
                    {...field}
                  />
                </FormControl>
                <TranslatedFormMessage error={fieldState.error} />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmedPassword"
            rules={{
              required: 'RegisterUserPanel.errors.confirmPasswordRequired',
              validate: value =>
                value === form.getValues('password') ||
                'RegisterUserPanel.errors.passwordsMismatch'
            }}
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="font-semibold text-fg-default">
                  {t('RegisterUserPanel.confirmPasswordLabel')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    size="md"
                    placeholder={t(
                      'RegisterUserPanel.confirmPasswordPlaceholder'
                    )}
                    {...field}
                  />
                </FormControl>
                <TranslatedFormMessage error={fieldState.error} />
              </FormItem>
            )}
          />

          <div className="flex justify-end pt-2">
            <FormSaveButton
              mutation={
                form.formState.isSubmitting ? mt.ofPending(null) : mt.ofIdle()
              }
              variant="primaryAlt"
              size="md"
              disabled={isLoading || form.formState.isSubmitting}
              saveText={t('RegisterUserPanel.continueButton')}
              showHotKey={false}
            />
          </div>
        </FormRootElement>
      </Form>
    </div>
  );
};

export default RegisterUserPanel;

// Custom FormMessage that translates error keys
const TranslatedFormMessage = ({ error }) => {
  const { t } = useTranslation('v1-features');

  if (!error) return null;

  const message = typeof error.message === 'string' ? error.message : '';

  // If the message is a translation key, translate it
  const displayMessage = message.startsWith('RegisterUserPanel.errors.')
    ? t(message)
    : message;

  return (
    <p className="text-base font-medium text-fg-danger">{displayMessage}</p>
  );
};
